import { Stack, useLocalSearchParams } from 'expo-router'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { useStore } from '@/models'
import { TournamentReentryPrompt } from '@/screens/tournament/TournamentReentryPrompt'
import { TournamentHostBotPrompt } from '@/screens/tournament/TournamentHostBotPrompt'
import { TournamentWinnerPrompt } from '@/screens/tournament/TournamentWinnerPrompt'

export const unstable_settings = {
  initialRouteName: 'index'
}

/**
 * Tournament screens are children of the lobby (`index`). All screens
 * use the default forward animation so user-driven navigation animates
 * right-to-left and back animates left-to-right.
 *
 * Engine redirects (bust-out, tournament-end, etc.) use `router.replace`
 * which doesn't animate, which is intended for state-machine transitions.
 *
 * The reentry + host-bot prompts are mounted here — and ONLY here — so a
 * busted host who's been redirected to /summary, /tables, /leaderboard,
 * or /index still receives the modal. The `/game/[gameId]` route is a
 * sibling, not a descendant, so its mount wouldn't survive the redirect
 * out of the table. Previously these were dual-mounted (here AND in
 * `GameContainer`); the GameContainer copy was removed to avoid
 * simultaneous-open ambiguity when a busted host had both routes
 * mounted (e.g. spectating their old table while the host-bot decision
 * fires).
 */
export default function TournamentIdLayout() {
  const { tournamentId, justFinished } = useLocalSearchParams<{ tournamentId: string; justFinished?: string }>()
  const userId = useStore().session.user?.auth0Id

  return (
    <>
      <Stack screenOptions={{ headerShown: false }} />
      {tournamentId && userId && (
        <>
          <TournamentReentryPrompt tournamentId={tournamentId as Id<'tournaments'>} userId={userId} />
          <TournamentHostBotPrompt tournamentId={tournamentId as Id<'tournaments'>} userId={userId} />
          <TournamentWinnerPrompt
            tournamentId={tournamentId as Id<'tournaments'>}
            skipAnnouncement={justFinished === '1'}
          />
        </>
      )}
    </>
  )
}
